import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef, useCallback } from "react";
/**
 * Core scrolling leaderboard list. Top N entries are always visible; the
 * remainder cycles in a scrolling section. Orientation-agnostic — consumer
 * picks the layout based on viewport.
 */
export function LeaderboardList({ totalCount, fixedCount = 5, cycleIntervalMs = 10000, renderEntry, }) {
    const containerRef = useRef(null);
    const [visibleCapacity, setVisibleCapacity] = useState(5);
    const [scrollOffset, setScrollOffset] = useState(0);
    const [animating, setAnimating] = useState(false);
    const firstRowRef = useRef(null);
    const [measuredRowHeight, setMeasuredRowHeight] = useState(0);
    useEffect(() => {
        if (!firstRowRef.current)
            return;
        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                setMeasuredRowHeight(entry.borderBoxSize[0]?.blockSize ?? entry.contentRect.height + 8);
            }
        });
        observer.observe(firstRowRef.current);
        return () => observer.disconnect();
    }, [totalCount]);
    useEffect(() => {
        if (!containerRef.current || measuredRowHeight === 0)
            return;
        const container = containerRef.current;
        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const height = entry.contentRect.height;
                // The row gap varies by breakpoint (8px base, 16px at bigscreen) —
                // read the computed value rather than assuming one.
                const gap = parseFloat(getComputedStyle(container).rowGap) || 8;
                const rowH = measuredRowHeight + gap;
                const fixedHeight = Math.min(fixedCount, totalCount) * rowH;
                const dividerHeight = 12;
                const remaining = Math.max(1, Math.floor((height - fixedHeight - dividerHeight) / rowH));
                setVisibleCapacity(remaining);
            }
        });
        observer.observe(container);
        return () => observer.disconnect();
    }, [fixedCount, totalCount, measuredRowHeight]);
    const scrollableCount = Math.max(0, totalCount - fixedCount);
    const scrollWindowSize = Math.min(visibleCapacity, scrollableCount);
    const cycle = useCallback(() => {
        if (scrollableCount <= visibleCapacity)
            return;
        setAnimating(true);
        setTimeout(() => {
            setScrollOffset((prev) => {
                const next = prev + scrollWindowSize;
                return next >= scrollableCount ? 0 : next;
            });
            setAnimating(false);
        }, 300);
    }, [scrollableCount, visibleCapacity, scrollWindowSize]);
    useEffect(() => {
        if (scrollableCount <= visibleCapacity)
            return;
        const timer = setInterval(cycle, cycleIntervalMs);
        return () => clearInterval(timer);
    }, [cycle, cycleIntervalMs, scrollableCount, visibleCapacity]);
    useEffect(() => {
        setScrollOffset(0);
    }, [totalCount]);
    const fixedEntries = Math.min(fixedCount, totalCount);
    const scrollingIndices = [];
    if (scrollableCount > 0) {
        const remaining = scrollableCount - scrollOffset;
        const pageSize = Math.min(scrollWindowSize, remaining);
        for (let i = 0; i < pageSize; i++) {
            scrollingIndices.push(fixedCount + scrollOffset + i);
        }
    }
    return (_jsxs("div", { ref: containerRef, className: "flex flex-col gap-2 bigscreen:gap-4 h-full overflow-hidden", children: [Array.from({ length: fixedEntries }, (_, i) => (_jsx("div", { ref: i === 0 ? firstRowRef : undefined, children: renderEntry(i) }, `fixed-${i}`))), scrollableCount > 0 && (_jsx("div", { className: "border-t border-gray-700/60 my-0.5" })), _jsx("div", { className: `flex flex-col gap-2 bigscreen:gap-4 transition-opacity duration-300 ${animating ? "opacity-0" : "opacity-100"}`, children: scrollingIndices.map((idx) => (_jsx("div", { className: "animate-scroll-up", children: renderEntry(idx) }, `scroll-${idx}`))) })] }));
}
//# sourceMappingURL=LeaderboardList.js.map